import { useCallback } from 'react';
import { createFileRoute, useParams } from '@tanstack/react-router';
import { Helmet } from 'react-helmet-async';
import { IntegrationsPage } from '@/features/integrations/integrations-page';
import type { GoogleCardIntent } from '@/features/integrations/google-business/google-business-profile-card';
import { BusinessSetupPageShell } from './business-setup/components/business-setup-page-shell';
import { formatDocumentTitle } from '@/lib/document-title';

interface IntegrationsSearch {
  /** Set by the Google sign-in callback: `choose` opens the listing picker, `connect` retries the sign-in. */
  google?: GoogleCardIntent;
}

export const Route = createFileRoute('/_auth/$orgId/settings/integrations')({
  validateSearch: (search: Record<string, unknown>): IntegrationsSearch => ({
    google: search.google === 'choose' || search.google === 'connect' ? search.google : undefined,
  }),
  component: IntegrationsRoute,
});

function IntegrationsRoute() {
  const { orgId } = useParams({ from: '/_auth/$orgId/settings/integrations' });
  const { google } = Route.useSearch();
  const navigate = Route.useNavigate();
  // One-shot: drop the param so a reload or Back doesn't reopen the picker or restart the sign-in.
  const clearGoogleIntent = useCallback(() => {
    void navigate({ search: (prev) => ({ ...prev, google: undefined }), replace: true });
  }, [navigate]);

  return (
    <>
      <Helmet defer={false}>
        <title>{formatDocumentTitle('Integrations')}</title>
      </Helmet>

      <BusinessSetupPageShell orgId={orgId} activePath="integrations">
        <IntegrationsPage googleIntent={google} onGoogleIntentHandled={clearGoogleIntent} />
      </BusinessSetupPageShell>
    </>
  );
}
